// Shared spaced-repetition primitives used by both:
//   1. The standalone Flashcards system (math facts, vocab, capitals) which
//      cycles cards on an HOURLY cadence — kids see the same card multiple
//      times per session while still learning.
//   2. The per-language vocabulary system which uses classic LEITNER day-based
//      intervals (0/1/2/4/7/14 days) for long-term retention.
//
// Both schedulers live here so a single tuning change propagates everywhere
// and the mastery threshold (3 consecutive correct) is consistent across the
// whole app. Each store still owns its persistent data shape — this module
// owns the *algorithm*.

// ── Mastery threshold — shared between modes ────────────────────────────────
export const MASTERY_STREAK_THRESHOLD = 3;

export function isMastered(streak: number): boolean {
  return streak >= MASTERY_STREAK_THRESHOLD;
}

// ── Daily Leitner ladder (used by per-language vocabulary) ──────────────────
// Each "level" maps to a number of days before the word should next be shown.
// Level 0 = needs to be reviewed today (just got it wrong); Level 5+ = long
// retention interval.
export const LEITNER_DAY_INTERVALS: readonly number[] = [0, 1, 2, 4, 7, 14] as const;

export function nextDailyReview(level: number, todayYMD: string): string {
  const idx = Math.max(0, Math.min(level, LEITNER_DAY_INTERVALS.length - 1));
  const interval = LEITNER_DAY_INTERVALS[idx];
  const d = new Date(todayYMD + 'T00:00:00');
  d.setDate(d.getDate() + interval);
  return d.toISOString().split('T')[0];
}

// ── Hourly cadence (used by Flashcards game) ────────────────────────────────
// Aggressive on misses (30 min) and scales linearly with streak — the "boxes"
// from the daily ladder are not appropriate here because kids practice math
// facts in single sessions and need same-session retries.
const HOUR_MS = 60 * 60 * 1000;
const MISS_HOURS = 0.5;       // 30 min after a wrong answer
const HOURS_PER_STREAK = 12;  // 12 hours per streak step

/**
 * Returns ISO timestamp for the next time this card should appear, given the
 * current streak (after applying the just-recorded answer) and whether the
 * answer was correct.
 */
export function nextHourlyReview(newStreak: number, correct: boolean, now: Date = new Date()): string {
  const hours = correct ? Math.max(1, newStreak) * HOURS_PER_STREAK : MISS_HOURS;
  return new Date(now.getTime() + hours * HOUR_MS).toISOString();
}
